import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Briefcase, ArrowLeft, DollarSign, TrendingUp, MapPin, ChevronDown } from 'lucide-react';

const WFH_ROLES = [
  { title: "Customer Service Representative", low: 30000, mid: 37000, high: 45000 },
  { title: "Chat Support Specialist", low: 28000, mid: 35000, high: 42000 },
  { title: "Data Entry Specialist", low: 27000, mid: 33000, high: 40000 },
  { title: "Virtual Assistant", low: 32000, mid: 42000, high: 55000 },
  { title: "Medical Coding Specialist", low: 40000, mid: 50000, high: 62000 },
  { title: "Technical Support Agent", low: 35000, mid: 43000, high: 55000 },
  { title: "Social Media Manager", low: 38000, mid: 48000, high: 62000 },
  { title: "Online Tutor", low: 30000, mid: 45000, high: 65000 },
  { title: "Web Developer", low: 55000, mid: 78000, high: 110000 },
  { title: "Bookkeeper / Accountant", low: 38000, mid: 50000, high: 65000 },
  { title: "Recruiter (Remote)", low: 42000, mid: 58000, high: 80000 },
  { title: "Transcriptionist", low: 25000, mid: 33000, high: 42000 },
  { title: "Insurance Agent (WFH)", low: 35000, mid: 50000, high: 72000 },
  { title: "Federal IT Specialist (Remote)", low: 60000, mid: 82000, high: 115000 },
  { title: "UX/UI Designer", low: 55000, mid: 75000, high: 105000 },
];

const STATE_COST_MULTIPLIER = {
  "National Average": 1.0,
  "California": 1.25, "New York": 1.22, "Massachusetts": 1.18, "Washington": 1.15,
  "Colorado": 1.08, "Oregon": 1.06, "New Jersey": 1.12, "Connecticut": 1.10,
  "Hawaii": 1.30, "Virginia": 1.05, "Maryland": 1.07, "Illinois": 1.04,
  "Florida": 0.97, "Texas": 0.95, "Georgia": 0.93, "North Carolina": 0.94,
  "Ohio": 0.90, "Michigan": 0.91, "Indiana": 0.88, "Tennessee": 0.90,
  "Arizona": 0.96, "Pennsylvania": 0.98, "Missouri": 0.87, "Alabama": 0.85,
  "Mississippi": 0.84, "Arkansas": 0.85, "Oklahoma": 0.86, "West Virginia": 0.83,
  "Kentucky": 0.86, "Iowa": 0.88, "Kansas": 0.88, "Nebraska": 0.89,
  "South Carolina": 0.91, "Louisiana": 0.89, "Utah": 1.00, "Nevada": 1.02,
  "Minnesota": 1.02, "Wisconsin": 0.92,
};

const fmt = (n) => "$" + Math.round(n).toLocaleString();

export default function SalaryCalculator() {
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedState, setSelectedState] = useState("National Average");
  const [experience, setExperience] = useState("mid");

  const mult = STATE_COST_MULTIPLIER[selectedState] || 1.0;
  const role = selectedRole ? WFH_ROLES.find(r => r.title === selectedRole) : null;

  const getSalary = () => {
    if (!role) return null;
    const base = experience === "entry" ? role.low : experience === "mid" ? role.mid : role.high;
    return Math.round(base * mult);
  };

  const salary = getSalary();

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <nav className="glass sticky top-0 z-50 border-b border-[#E5E0D8]/50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-[#047857] flex items-center justify-center"><Briefcase size={14} className="text-white" strokeWidth={1.5} /></div>
            <span className="text-lg font-semibold tracking-tight" style={{fontFamily:'Outfit'}}>WorkFromJoy</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/jobs" className="text-sm text-[#1C2A24]/70 hover:text-[#047857]">Find Jobs</Link>
            <Link to="/blog" className="text-sm text-[#1C2A24]/70 hover:text-[#047857]">Blog</Link>
            <Link to="/auth"><Button className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white h-9 px-5 text-sm" data-testid="salary-signup">Sign Up Free</Button></Link>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-6 md:px-12 py-10">
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-[#047857] mb-6 hover:opacity-80"><ArrowLeft size={14} strokeWidth={1.5} /> Back to Home</Link>

        <div className="mb-10">
          <h1 className="text-3xl md:text-4xl tracking-tight font-medium" style={{fontFamily:'Outfit'}}>WFH Salary Calculator</h1>
          <p className="text-[#1C2A24]/60 mt-2">Compare remote job salaries by role, experience, and state cost-of-living.</p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Input Panel */}
          <div className="space-y-6">
            {/* Role Selection */}
            <div>
              <label className="text-sm font-medium text-[#1C2A24]/80 mb-2 block" style={{fontFamily:'Outfit'}}>Select a WFH Role</label>
              <div className="relative">
                <select value={selectedRole || ""} onChange={e => setSelectedRole(e.target.value || null)}
                  className="w-full h-12 rounded-2xl bg-white border border-[#E5E0D8] px-4 text-sm appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#047857]" data-testid="salary-role-select">
                  <option value="">Choose a role...</option>
                  {WFH_ROLES.map(r => <option key={r.title} value={r.title}>{r.title}</option>)}
                </select>
                <ChevronDown size={14} className="absolute right-4 top-1/2 -translate-y-1/2 text-[#1C2A24]/30 pointer-events-none" />
              </div>
            </div>

            {/* State */}
            <div>
              <label className="text-sm font-medium text-[#1C2A24]/80 mb-2 block" style={{fontFamily:'Outfit'}}>Your State</label>
              <div className="relative">
                <select value={selectedState} onChange={e => setSelectedState(e.target.value)}
                  className="w-full h-12 rounded-2xl bg-white border border-[#E5E0D8] px-4 text-sm appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#047857]" data-testid="salary-state-select">
                  {Object.keys(STATE_COST_MULTIPLIER).map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <ChevronDown size={14} className="absolute right-4 top-1/2 -translate-y-1/2 text-[#1C2A24]/30 pointer-events-none" />
              </div>
            </div>

            {/* Experience */}
            <div>
              <label className="text-sm font-medium text-[#1C2A24]/80 mb-3 block" style={{fontFamily:'Outfit'}}>Experience Level</label>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { key: "entry", label: "Entry", desc: "0-1 years" },
                  { key: "mid", label: "Mid-Level", desc: "2-4 years" },
                  { key: "senior", label: "Senior", desc: "5+ years" },
                ].map(lvl => (
                  <button key={lvl.key} onClick={() => setExperience(lvl.key)}
                    className={`rounded-2xl border p-4 text-left transition-all ${experience === lvl.key ? 'border-[#047857] bg-[#047857]/5' : 'border-[#E5E0D8] bg-white hover:border-[#047857]/30'}`}
                    data-testid={`salary-exp-${lvl.key}`}>
                    <p className="text-sm font-medium" style={{fontFamily:'Outfit'}}>{lvl.label}</p>
                    <p className="text-xs text-[#1C2A24]/40 mt-0.5">{lvl.desc}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Result Panel */}
          <div>
            {salary ? (
              <div className="bg-white rounded-3xl border border-[#E5E0D8] p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="salary-result">
                <div className="flex items-center gap-2 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-[#047857]/10 flex items-center justify-center">
                    <DollarSign size={18} className="text-[#047857]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-sm font-medium" style={{fontFamily:'Outfit'}}>Estimated Salary</p>
                    <p className="text-xs text-[#1C2A24]/40">{selectedState} adjusted</p>
                  </div>
                </div>

                <p className="text-4xl font-semibold text-[#047857] mb-1" style={{fontFamily:'Outfit'}} data-testid="salary-amount">{fmt(salary)}<span className="text-base font-normal text-[#1C2A24]/40">/year</span></p>
                <p className="text-sm text-[#1C2A24]/50 mb-6">{fmt(salary / 12)}/month | {fmt(salary / 26)}/biweekly</p>

                {/* Salary Range Bar */}
                <div className="mb-6">
                  <div className="flex justify-between text-xs text-[#1C2A24]/40 mb-2">
                    <span>{fmt(role.low * mult)}</span>
                    <span>{fmt(role.high * mult)}</span>
                  </div>
                  <div className="h-3 bg-[#F4F1E8] rounded-full overflow-hidden">
                    <div className="h-full rounded-full bg-gradient-to-r from-[#86A789] via-[#047857] to-[#D97757] transition-all duration-500"
                      style={{ width: `${Math.min(100, Math.max(10, ((salary - role.low * mult) / ((role.high - role.low) * mult)) * 100))}%` }} />
                  </div>
                  <div className="flex justify-between text-xs text-[#1C2A24]/30 mt-1">
                    <span>Entry</span>
                    <span>Senior</span>
                  </div>
                </div>

                {selectedState !== "National Average" && (
                  <div className="bg-[#F4F1E8]/50 rounded-xl p-4 mb-4">
                    <div className="flex items-center gap-2">
                      <MapPin size={12} className="text-[#047857]" strokeWidth={1.5} />
                      <span className="text-xs text-[#1C2A24]/60">
                        {mult > 1 ? `${selectedState} salaries are typically ${Math.round((mult - 1) * 100)}% above` : `${selectedState} salaries are typically ${Math.round((1 - mult) * 100)}% below`} the national average.
                      </span>
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-2 text-xs text-[#1C2A24]/40">
                  <TrendingUp size={12} strokeWidth={1.5} />
                  <span>WFH salaries growing 8-12% annually in this category</span>
                </div>

                <div className="mt-6 pt-6 border-t border-[#E5E0D8]">
                  <Link to="/jobs"><Button className="w-full rounded-full bg-[#047857] hover:bg-[#065F46] text-white h-11" data-testid="salary-find-jobs">Find {selectedRole} Jobs</Button></Link>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-3xl border border-[#E5E0D8] p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] flex flex-col items-center justify-center min-h-[300px]">
                <div className="w-16 h-16 rounded-2xl bg-[#F4F1E8] flex items-center justify-center mb-4">
                  <DollarSign size={24} className="text-[#1C2A24]/30" strokeWidth={1.5} />
                </div>
                <p className="text-sm text-[#1C2A24]/50 text-center" style={{fontFamily:'Outfit'}}>Select a role to see estimated salary</p>
              </div>
            )}
          </div>
        </div>

        {/* Popular Roles Table */}
        <div className="mt-12" data-testid="salary-table">
          <h2 className="text-lg font-medium mb-4" style={{fontFamily:'Outfit'}}>All WFH Roles — Salary Overview</h2>
          <div className="bg-white rounded-2xl border border-[#E5E0D8] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#E5E0D8] bg-[#F4F1E8]/50">
                    <th className="text-left px-5 py-3 font-medium text-[#1C2A24]/60" style={{fontFamily:'Outfit'}}>Role</th>
                    <th className="text-right px-5 py-3 font-medium text-[#1C2A24]/60" style={{fontFamily:'Outfit'}}>Entry</th>
                    <th className="text-right px-5 py-3 font-medium text-[#1C2A24]/60" style={{fontFamily:'Outfit'}}>Mid</th>
                    <th className="text-right px-5 py-3 font-medium text-[#1C2A24]/60" style={{fontFamily:'Outfit'}}>Senior</th>
                  </tr>
                </thead>
                <tbody>
                  {WFH_ROLES.map((r, i) => (
                    <tr key={i} className="border-b border-[#E5E0D8]/50 hover:bg-[#F4F1E8]/30 cursor-pointer transition-colors" onClick={() => { setSelectedRole(r.title); window.scrollTo({top: 0, behavior: 'smooth'}); }}>
                      <td className="px-5 py-3 font-medium text-[#1C2A24]">{r.title}</td>
                      <td className="px-5 py-3 text-right text-[#1C2A24]/60">{fmt(r.low * mult)}</td>
                      <td className="px-5 py-3 text-right text-[#047857] font-medium">{fmt(r.mid * mult)}</td>
                      <td className="px-5 py-3 text-right text-[#1C2A24]/60">{fmt(r.high * mult)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <p className="text-xs text-[#1C2A24]/30 mt-3">Salary data based on 2025-2026 WFH market averages. Adjusted for {selectedState} cost of living.</p>
        </div>
      </div>
    </div>
  );
}
